import { useMutation } from '@tanstack/react-query';
import { type FormEvent, type ReactElement, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { Button } from '@autop/ui/src/button';
import { Field } from '@autop/ui/src/field';

import { ResendVerificationForm } from '@/features/resend-verification';
import { SocialLoginButtons } from '@/features/social-login';
import { useSession } from '@/entities/session';
import { isEmailNotVerified, setAccessToken, useTRPC } from '@/shared/api';
import { toInternalPath } from '@/shared/lib/internal-path';
import { EMAIL_SIGNIN, rememberLastSignin } from '@/shared/lib/last-signin';
import { isAdminLanding, signinLanding } from '@/shared/lib/signin-landing';
import { PasswordField } from '@/shared/ui/password-field';

/** 서버가 준 문구가 없을 때 보여줄 말. 영어 원문(`Failed to fetch`)을 그대로 띄우지 않는다. */
const FALLBACK_MESSAGE = '로그인하지 못했습니다. 잠시 후 다시 시도해 주세요.';

/**
 * 서버 스키마와 같은 값 (`apps/api/src/modules/auth/dto/auth.schema.ts`).
 *
 * 비밀번호에 **최소 길이를 걸지 않는다** — 로그인 화면이 "정책을 만족하는 값인지"
 * 알려주는 창구가 되면 안 되고, 짧은 값도 정상적으로 틀린 비밀번호로 처리돼야 한다.
 * 서버가 같은 이유로 최소 길이를 뺐다.
 */
const MAX_PASSWORD_LENGTH = 128;
const MAX_EMAIL_LENGTH = 255;

/**
 * 로그인 실패.
 *
 * `canResend` 는 상태코드가 아니라 **서버가 실어 보낸 사유 코드**로 정한다 —
 * 미인증·차단·탈퇴가 모두 403 이라, 상태코드로 판단하면 차단된 사람에게도
 * "인증 메일 다시 받기"를 권하게 된다.
 */
type Failure = {
  message: string;
  canResend: boolean;
  /**
   * 그 실패가 일어난 주소.
   *
   * 화면의 입력값을 재발송 창구에 그대로 넘기지 않는 이유 — 사용자가 주소를 고쳐
   * 다시 시도하면 그때는 **새 실패의 주소**를 들어야 한다. 실패에 주소를 함께 담아 두면
   * 둘이 어긋날 자리가 없어진다.
   */
  email: string;
};

const toMessage = (error: unknown): string =>
  error instanceof Error && error.message.length > 0 ? error.message : FALLBACK_MESSAGE;

/**
 * 로그인 — 와이어프레임 P-01 (`/login`).
 *
 * 중앙 정렬 카드로 이탈 경로를 줄인다. 이메일 폼을 먼저 두고 소셜을 그 **아래**에 둔다 —
 * 회원가입 화면과 같은 배치다. 두 화면을 오갈 때 버튼 위치가 바뀌면 매번 다시 찾게 된다.
 *
 * 실패 문구는 **서버가 준 것을 그대로** 보여준다. 로그인 실패는 없는 계정·틀린 비밀번호·
 * 소셜 전용 회원을 구분하지 않는데(계정 열거 방지), 화면이 문구를 지어내면 서버가 애써
 * 맞춰 둔 모호함이 그 자리에서 깨진다.
 */
export function LoginPage(): ReactElement {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // 주소창의 값이라 그대로 믿지 않는다 — 로그인 직후의 이동이라 속으면 남의 사이트에 내려앉는다.
  const next = toInternalPath(params.get('next'), '/library');

  const signIn = useSession((state) => state.signIn);
  const { mutateAsync, isPending } = useMutation(trpc.auth.signin.mutationOptions());

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [failure, setFailure] = useState<Failure | null>(null);

  /**
   * 연타 방어. `isPending` 은 뮤테이션 구독을 거쳐 갱신되므로, 클릭과 버튼이 실제로
   * 잠기는 사이에 틈이 있다. 그 틈으로 두 번째 제출이 들어가면 같은 자격증명으로
   * 로그인을 두 번 시도하고, 시도 제한만 그만큼 축낸다.
   */
  const isSubmitting = useRef(false);

  /**
   * 결과를 `useMutation` 의 상태가 아니라 **`mutateAsync` 의 약속**으로 받는다.
   *
   * 뮤테이션 상태는 관찰자 구독에 실려 오는데, 그 구독이 끊겼다 붙는 틈에 끝난 결과를
   * 받지 못하는 자리가 있다(`/verify` 가 성공하고도 대기 화면에 멈춘 적이 있다).
   * 약속은 구독과 무관하게 끝나므로 그 값으로 화면을 정한다.
   */
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    setFailure(null);

    try {
      const session = await mutateAsync({ email, password });

      // 순서가 중요하다 — 토큰을 먼저 담아야 다음 화면의 첫 요청이 인증 헤더를 싣는다.
      setAccessToken(session.accessToken);
      signIn(session.member);
      rememberLastSignin(EMAIL_SIGNIN);

      // 운영자는 자기 화면으로 곧장 보낸다. 라우터가 아니라 window.location 인 이유 —
      //   운영자 화면은 이 앱의 라우트가 아니다(운영은 nginx 가 /admin 으로, dev 는
      //   자기 포트로 서빙). navigate 로 보내면 web 의 404 가 뜬다.
      if (isAdminLanding(session.member.role, params.get('next') !== null)) {
        window.location.assign(__ADMIN_URL__);
        return;
      }

      // 온보딩을 안 지났으면 그 화면을 먼저 거친다 — 원래 가려던 곳은 물고 간다.
      void navigate(signinLanding(next, session.member.onboarding.isCompleted), { replace: true });
    } catch (error) {
      setFailure({ message: toMessage(error), canResend: isEmailNotVerified(error), email });
    } finally {
      isSubmitting.current = false;
    }
  };

  return (
    <div className="u-container flex justify-center py-16">
      <div className="flex w-full max-w-[26rem] flex-col gap-6">
        <h1 className="text-center text-[1.625rem] font-bold tracking-[-0.04em]">로그인</h1>

        <form
          // 실패하면 이 아래에 재발송 폼이 함께 뜬다. 두 폼의 「이메일」 입력이 같은 이름을
          //   갖게 되므로, 폼에도 이름을 줘야 화면 낭독기에서 구분된다.
          aria-label="로그인"
          className="flex flex-col gap-4"
          onSubmit={(event) => void submit(event)}
        >
          <Field
            id="login-email"
            name="email"
            type="email"
            label="이메일"
            placeholder="name@example.com"
            required
            maxLength={MAX_EMAIL_LENGTH}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <PasswordField
            id="login-password"
            name="password"
            label="비밀번호"
            placeholder="••••••••"
            required
            maxLength={MAX_PASSWORD_LENGTH}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          <div className="flex justify-end text-[13px]">
            <Link to="/reset-password" className="underline underline-offset-4">
              비밀번호 재설정
            </Link>
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={isPending}>
            로그인
          </Button>
        </form>

        {failure === null ? null : <FailureNotice failure={failure} />}

        <div className="flex items-center gap-3 text-[12.5px] text-muted-foreground">
          <span className="h-px flex-1 bg-border" />
          또는 간편하게
          <span className="h-px flex-1 bg-border" />
        </div>

        <SocialLoginButtons intent="login" next={next} />

        <p className="text-center text-[13px] text-muted-foreground">
          아직 계정이 없으신가요?{' '}
          <Link to="/signup" className="font-medium text-foreground underline underline-offset-4">
            회원가입
          </Link>
        </p>
      </div>
    </div>
  );
}

/**
 * 실패 안내.
 *
 * 아직 인증하지 않은 계정이면 **여기서 바로 인증 메일을 다시 받게 한다.** 그 창구가 없으면
 * 사용자는 막다른 길에 선다 — 서버 문구가 "받은 인증 메일의 링크를 눌러 주세요"인데,
 * 메일이 사라졌거나 만료됐으면 할 수 있는 일이 없다.
 *
 * 반대로 차단·탈퇴에는 창구를 열지 않는다. 재발송으로 풀리는 상태가 아니라,
 * 안내가 곧 할 수 없는 일을 시키는 것이 된다.
 */
function FailureNotice({ failure }: { failure: Failure }): ReactElement {
  return (
    <div className="flex flex-col gap-4 rounded-md border border-destructive border-l-[3px] px-4 py-3">
      <p role="alert" className="text-[13px] text-destructive">
        {failure.message}
      </p>
      {failure.canResend ? (
        // 화면의 입력값이 아니라 **그 실패의 주소**를 넘긴다. 재발송 폼은 처음 받은 주소만
        //   초기값으로 쓰므로, 주소를 고쳐 다시 시도했을 때 두 값이 어긋나면 사용자가
        //   모르는 주소로 인증 메일이 나간다. 제출할 때마다 실패를 비우므로(setFailure(null))
        //   이 폼은 실패마다 새로 마운트되고, 그래서 항상 마지막 실패의 주소를 든다.
        <ResendVerificationForm defaultEmail={failure.email} />
      ) : null}
    </div>
  );
}
